import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import {
	Avatar,
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	FormControl,
	FormControlLabel,
	IconButton,
	MenuItem,
	Select,
	Tooltip,
	Typography,
	useTheme,
	type SelectChangeEvent,
	type SxProps,
	type Theme,
} from '@mui/material';
import i18n, { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { leagueLogoAvatarSx } from '../../components/custom/avatar/LeagueAvatar';
import CustomCalendarDialog from '../../components/custom/dialog/CustomCalendarDialog';
import CustomSuccessButton from '../../components/custom/btn/CustomSuccessButton';
import { showErrorSnackbar, showSuccessSnackbar } from '../../components/custom/snackbar/snackbarSlice';
import { pathToLogoImage } from '../../components/utils/imgBase64Converter';
import { resolveTeamDisplayName } from '../../components/utils/teamDisplay';
import MatchTeamsWithLogos from '../../components/MatchTeamsWithLogos';
import { copyText } from '../api-sandbox/CopyableValue';
import BetsPagination from '../bets/BetsPagination';
import { externalDataLayerAccent } from '../../shared/externalDataLayerColors';
import { useFormatUserDateTime } from '../../shared/useFormatUserDateTime';
import {
	archiveAllErrorLogs,
	archiveErrorLog,
	clearArchivedErrorLogs,
	clearErrorLogs,
	DEFAULT_ERROR_LOGS_PAGE_SIZE,
	deleteErrorLog,
	ERROR_LOGS_PAGE_SIZES,
	fetchErrorLogs,
	fetchErrorLogsCount,
	unarchiveErrorLog,
	type ErrorLogEntry,
	type ErrorLogsPageSize,
} from './errorLogsApi';
import {
	ERROR_LOG_ACCENT,
	ERROR_LOG_LEAGUE,
	ERROR_LOG_PROVIDER,
	ERROR_LOG_TEAMS,
	ERROR_LOG_WARN,
	chipIdSx,
	chipSx,
	chipWithLogosSx,
	errorLogCardSx,
	errorLogChipLogoSx,
	errorLogMessageSx,
	errorLogMetaRowSx,
	errorLogOccurrenceRowSx,
	errorLogOccurrencesListSx,
	errorLogOccurrencesToggleSx,
	errorLogTimeSx,
	errorLogsPageRootSx,
	errorLogsPageSizeSelectSx,
	errorLogsTitleSx,
	errorLogsToolbarActionBtnSx,
	errorLogsToolbarActionsSx,
	errorLogsToolbarSx,
	errorLogsToolbarTopSx,
} from './errorLogsPageStyles';

function codeLabel(code: string): string {
	const key = `errorLogs.code.${code}`;
	return t(key) !== key ? t(key) : code;
}

function layerLabel(layer: string): string {
	const key = `errorLogs.layer.${layer}`;
	return t(key) !== key ? t(key) : layer;
}

/** Drop bracket team labels from mappingFailures when logos are rendered separately. */
function formatErrorLogMessage(message: string | null | undefined, hideMappingLabels: boolean): string | null {
	if (!message || !message.trim()) {
		return null;
	}
	if (!hideMappingLabels) {
		return message;
	}
	return message
		.split(';')
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => {
			if (!part.startsWith('mappingFailures=')) {
				return part;
			}
			return part.replace(/\s*\[[^\]]*]\s*$/, '').trim();
		})
		.join('; ');
}

function parseFailedMatchesFromMessage(
	message: string | null | undefined
): Array<{
	matchScheduleId?: string | null;
	homeTitle?: string;
	awayTitle?: string;
	homeLogoKey?: string;
	awayLogoKey?: string;
}> {
	if (!message) {
		return [];
	}
	const match = /mappingFailures=\d+\s*\[([^\]]+)]/.exec(message);
	if (!match?.[1]) {
		return [];
	}
	return match[1]
		.split(',')
		.map((pair) => pair.trim())
		.filter(Boolean)
		.map((pair) => {
			const parts = pair.split(/\s+-\s+/);
			const homeTitle = parts[0]?.trim() || pair;
			const awayTitle = parts[1]?.trim() || '';
			return {
				homeTitle,
				awayTitle,
				homeLogoKey: homeTitle,
				awayLogoKey: awayTitle || undefined,
			};
		});
}

function ErrorLogChipLogo({ src, alt }: { src: string; alt: string }): JSX.Element {
	return (
		<Avatar
			variant="square"
			src={src}
			alt={alt}
			sx={[errorLogChipLogoSx, leagueLogoAvatarSx] as SxProps<Theme>}
		/>
	);
}

function occurrenceTimes(entry: ErrorLogEntry): string[] {
	if (entry.occurredAt && entry.occurredAt.length > 0) {
		return entry.occurredAt;
	}
	const first = entry.firstOccurredAt ?? entry.createdAt;
	return first ? [first] : [];
}

function formatOccurrenceGap(ms: number): string {
	const sec = Math.max(0, Math.round(ms / 1000));
	if (sec < 60) {
		return t('errorLogsOccurrenceDeltaSec', { count: sec });
	}
	const minutesTotal = Math.round(sec / 60);
	if (minutesTotal < 60) {
		return t('errorLogsOccurrenceDeltaMin', { count: minutesTotal });
	}
	const hours = Math.floor(sec / 3600);
	const minutes = Math.round((sec % 3600) / 60);
	return t('errorLogsOccurrenceDeltaHm', { hours, minutes });
}

function OccurrenceHistory({
	times,
	formatDetailed,
}: {
	times: string[];
	formatDetailed: (iso?: string | null) => string;
}): JSX.Element | null {
	const [open, setOpen] = useState(false);
	const listRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (open && listRef.current) {
			listRef.current.scrollTop = listRef.current.scrollHeight;
		}
	}, [open]);

	if (times.length <= 1) {
		return null;
	}

	return (
		<>
			<Box
				role="button"
				tabIndex={0}
				aria-expanded={open}
				aria-label={t('errorLogsOccurrencesToggle', { count: times.length })}
				onClick={() => setOpen((prev) => !prev)}
				onKeyDown={(event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						setOpen((prev) => !prev);
					}
				}}
				sx={errorLogOccurrencesToggleSx}
			>
				<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
					{t('errorLogsOccurrencesToggle', { count: times.length })}
				</Typography>
				<ExpandMoreIcon
					fontSize="small"
					sx={{
						transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
						transition: 'transform 0.2s ease',
					}}
				/>
			</Box>
			<Collapse in={open} timeout={180} unmountOnExit>
				<Box ref={listRef} sx={errorLogOccurrencesListSx}>
					{times.map((iso, index) => {
						const prev = index > 0 ? Date.parse(times[index - 1]) : Number.NaN;
						const curr = Date.parse(iso);
						const gap =
							index > 0 && Number.isFinite(prev) && Number.isFinite(curr)
								? formatOccurrenceGap(curr - prev)
								: null;
						return (
							<Box key={`${iso}-${index}`} sx={errorLogOccurrenceRowSx}>
								<Typography component="span" sx={{ fontSize: 'inherit' }}>
									{formatDetailed(iso)}
								</Typography>
								{gap ? (
									<Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 600, flexShrink: 0 }}>
										{gap}
									</Typography>
								) : null}
							</Box>
						);
					})}
				</Box>
			</Collapse>
		</>
	);
}

type ConfirmDialogMode = 'clearActive' | 'archiveAll' | 'clearArchive';

export default function ErrorLogsPage(): JSX.Element {
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const { formatDetailed } = useFormatUserDateTime();
	const [loading, setLoading] = useState(true);
	const [entries, setEntries] = useState<ErrorLogEntry[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [activeCount, setActiveCount] = useState(0);
	const [archivedCount, setArchivedCount] = useState(0);
	const [includeArchived, setIncludeArchived] = useState(false);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState<ErrorLogsPageSize>(DEFAULT_ERROR_LOGS_PAGE_SIZE);
	const [confirmMode, setConfirmMode] = useState<ConfirmDialogMode | null>(null);
	const [confirming, setConfirming] = useState(false);

	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [list, counts] = await Promise.all([
				fetchErrorLogs({ page: page - 1, size: pageSize, includeArchived }),
				fetchErrorLogsCount(includeArchived),
			]);
			setEntries(list);
			setTotalCount(counts.count);
			setActiveCount(counts.activeCount);
			setArchivedCount(counts.archivedCount);
			const maxPage = Math.max(1, Math.ceil(counts.count / pageSize));
			if (page > maxPage) {
				setPage(maxPage);
			}
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'errorLogsLoadFailed',
				})
			);
		} finally {
			setLoading(false);
		}
	}, [dispatch, includeArchived, page, pageSize]);

	useEffect(() => {
		void load();
	}, [load]);

	const handlePageSizeChange = (event: SelectChangeEvent<number>): void => {
		const next = Number(event.target.value) as ErrorLogsPageSize;
		setPageSize(next);
		setPage(1);
	};

	const handleIncludeArchivedChange = (_: unknown, checked: boolean): void => {
		setIncludeArchived(checked);
		setPage(1);
	};

	const reloadAfterMutation = async (nextTotalHint?: number): Promise<void> => {
		if (nextTotalHint != null) {
			const maxPage = Math.max(1, Math.ceil(nextTotalHint / pageSize));
			if (page > maxPage) {
				setPage(maxPage);
				return;
			}
		}
		await load();
	};

	const handleDelete = async (id: string): Promise<void> => {
		try {
			await deleteErrorLog(id);
			dispatch(showSuccessSnackbar({ message: t('errorLogsEntryDeleted') }));
			await reloadAfterMutation(Math.max(0, totalCount - 1));
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'errorLogsDeleteFailed',
				})
			);
		}
	};

	const handleArchive = async (id: string): Promise<void> => {
		try {
			await archiveErrorLog(id);
			dispatch(showSuccessSnackbar({ message: t('errorLogsEntryArchived') }));
			const nextHint = includeArchived ? totalCount : Math.max(0, totalCount - 1);
			await reloadAfterMutation(nextHint);
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'errorLogsArchiveFailed',
				})
			);
		}
	};

	const handleUnarchive = async (id: string): Promise<void> => {
		try {
			await unarchiveErrorLog(id);
			dispatch(showSuccessSnackbar({ message: t('errorLogsEntryUnarchived') }));
			await reloadAfterMutation();
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'errorLogsUnarchiveFailed',
				})
			);
		}
	};

	const handleConfirm = async (): Promise<void> => {
		if (!confirmMode) {
			return;
		}
		setConfirming(true);
		try {
			if (confirmMode === 'clearActive') {
				await clearErrorLogs();
				dispatch(showSuccessSnackbar({ message: t('errorLogsCleared') }));
			} else if (confirmMode === 'archiveAll') {
				await archiveAllErrorLogs();
				dispatch(showSuccessSnackbar({ message: t('errorLogsArchivedAll') }));
			} else {
				await clearArchivedErrorLogs();
				dispatch(showSuccessSnackbar({ message: t('errorLogsArchiveCleared') }));
			}
			setConfirmMode(null);
			if (page === 1) {
				await load();
			} else {
				setPage(1);
			}
		} catch (error) {
			const fallback =
				confirmMode === 'clearActive'
					? 'errorLogsClearFailed'
					: confirmMode === 'archiveAll'
						? 'errorLogsArchiveAllFailed'
						: 'errorLogsClearArchiveFailed';
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : fallback,
				})
			);
		} finally {
			setConfirming(false);
		}
	};

	const confirmTitle =
		confirmMode === 'clearActive'
			? t('errorLogsClearAllTitle')
			: confirmMode === 'archiveAll'
				? t('errorLogsArchiveAllTitle')
				: t('errorLogsClearArchiveTitle');
	const confirmHelper =
		confirmMode === 'clearActive'
			? t('errorLogsClearAllHelper', { count: activeCount })
			: confirmMode === 'archiveAll'
				? t('errorLogsArchiveAllHelper', { count: activeCount })
				: t('errorLogsClearArchiveHelper', { count: archivedCount });
	const confirmAccept =
		confirmMode === 'clearActive'
			? t('errorLogsClearAll')
			: confirmMode === 'archiveAll'
				? t('errorLogsArchiveAll')
				: t('errorLogsClearArchive');

	return (
		<Box sx={errorLogsPageRootSx}>
			<Typography component="h1" sx={errorLogsTitleSx}>
				{t('errorLogsTitleWithCount', { count: totalCount })}
			</Typography>

			<Box sx={errorLogsToolbarSx}>
				<Box sx={errorLogsToolbarTopSx}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
						<Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
							{t('errorLogsPageSize')}
						</Typography>
						<FormControl size="small" sx={{ minWidth: '4.5rem' }}>
							<Select
								value={pageSize}
								onChange={handlePageSizeChange}
								displayEmpty
								aria-label={t('errorLogsPageSize')}
								sx={errorLogsPageSizeSelectSx}
							>
								{ERROR_LOGS_PAGE_SIZES.map((size) => (
									<MenuItem key={size} value={size}>
										{size}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={includeArchived}
									onChange={handleIncludeArchivedChange}
									disabled={loading}
								/>
							}
							label={
								<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
									{t('errorLogsShowArchived')}
								</Typography>
							}
							sx={{ ml: 0.25, mr: 0 }}
						/>
					</Box>
					<span>
						<Tooltip title={t('errorLogsRefresh')}>
							<span>
								<IconButton
									aria-label={t('errorLogsRefresh')}
									onClick={() => void load()}
									disabled={loading}
									size="small"
									sx={{ minWidth: 40, minHeight: 40 }}
								>
									{loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
								</IconButton>
							</span>
						</Tooltip>
					</span>
				</Box>
				<Box sx={errorLogsToolbarActionsSx}>
					<CustomSuccessButton
						buttonText={t('errorLogsArchiveAll')}
						onClick={() => setConfirmMode('archiveAll')}
						disabled={activeCount === 0 || loading}
						textSize="0.75rem"
						sx={errorLogsToolbarActionBtnSx}
					/>
					<CustomSuccessButton
						buttonText={t('errorLogsClearAll')}
						onClick={() => setConfirmMode('clearActive')}
						disabled={activeCount === 0 || loading}
						textSize="0.75rem"
						sx={errorLogsToolbarActionBtnSx}
					/>
					<CustomSuccessButton
						buttonText={t('errorLogsClearArchive')}
						onClick={() => setConfirmMode('clearArchive')}
						disabled={archivedCount === 0 || loading}
						textSize="0.75rem"
						sx={errorLogsToolbarActionBtnSx}
					/>
				</Box>
			</Box>

			<BetsPagination
				page={page}
				totalPages={totalPages}
				onPageChange={setPage}
				navigationAriaLabel="errorLogsPaginationNav"
			/>

			{loading && entries.length === 0 ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
					<CircularProgress size={32} />
				</Box>
			) : null}

			{!loading && entries.length === 0 ? (
				<Box
					sx={{
						textAlign: 'center',
						py: 5,
						px: 2,
						borderRadius: 2.5,
						border: '1px dashed',
						borderColor: 'divider',
						color: 'text.secondary',
					}}
				>
					<Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t('errorLogsEmptyTitle')}</Typography>
					<Typography variant="body2">
						{includeArchived && activeCount === 0 && archivedCount === 0
							? t('errorLogsEmptyArchived')
							: t('errorLogsEmpty')}
					</Typography>
				</Box>
			) : null}

			{entries.map((entry) => {
				const severity = (entry.severity || 'ERROR').toUpperCase();
				const archived = Boolean(entry.archived);
				const homeTitle = entry.homeTeamTitle || entry.homeTeam || '';
				const awayTitle = entry.awayTeamTitle || entry.awayTeam || '';
				const hasTeams = Boolean(homeTitle || awayTitle);
				const homeTeamName = resolveTeamDisplayName({ title: homeTitle }, i18n.language);
				const awayTeamName = resolveTeamDisplayName({ title: awayTitle }, i18n.language);
				const teamsLabel =
					homeTeamName && awayTeamName
						? `${homeTeamName} - ${awayTeamName}`
						: homeTeamName || awayTeamName;
				const failedMatches =
					entry.failedMatches && entry.failedMatches.length > 0
						? entry.failedMatches
						: parseFailedMatchesFromMessage(entry.message);
				const displayMessage = formatErrorLogMessage(entry.message, failedMatches.length > 0);
				const times = occurrenceTimes(entry);
				const firstAt = times[0] ?? entry.createdAt;
				const repeatCount = Math.max(entry.occurrenceCount ?? 0, times.length);
				return (
					<Box
						key={entry.id}
						sx={[
							errorLogCardSx(theme, severity),
							archived ? { opacity: 0.72 } : null,
						] as SxProps<Theme>}
					>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'flex-start',
								gap: 1,
								pl: 0.75,
							}}
						>
							<Box sx={{ minWidth: 0, flex: 1 }}>
								<Typography sx={errorLogTimeSx}>{formatDetailed(firstAt)}</Typography>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.75,
										mt: 0.35,
										flexWrap: 'wrap',
									}}
								>
									<Typography
										sx={{
											fontWeight: 700,
											fontSize: '0.95rem',
											color: severity === 'WARN' ? ERROR_LOG_WARN : ERROR_LOG_ACCENT,
										}}
									>
										{codeLabel(entry.code)}
									</Typography>
									{archived ? (
										<Chip
											size="small"
											label={t('errorLogsArchivedChip')}
											sx={chipSx('#64748b', theme)}
										/>
									) : null}
									{repeatCount > 1 ? (
										<Chip
											size="small"
											label={t('errorLogsRepeatChip', { count: repeatCount })}
											sx={chipSx(
												severity === 'WARN' ? ERROR_LOG_WARN : ERROR_LOG_ACCENT,
												theme
											)}
										/>
									) : null}
								</Box>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
								{archived ? (
									<span>
										<Tooltip title={t('errorLogsUnarchiveEntry')}>
											<span>
												<IconButton
													aria-label={t('errorLogsUnarchiveEntry')}
													size="small"
													onClick={() => void handleUnarchive(entry.id)}
													sx={{ minWidth: 40, minHeight: 40 }}
												>
													<UnarchiveOutlinedIcon fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
									</span>
								) : (
									<>
										<span>
											<Tooltip title={t('errorLogsArchiveEntry')}>
												<span>
													<IconButton
														aria-label={t('errorLogsArchiveEntry')}
														size="small"
														onClick={() => void handleArchive(entry.id)}
														sx={{ minWidth: 40, minHeight: 40 }}
													>
														<ArchiveOutlinedIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</span>
										<span>
											<Tooltip title={t('errorLogsDeleteEntry')}>
												<span>
													<IconButton
														aria-label={t('errorLogsDeleteEntry')}
														size="small"
														onClick={() => void handleDelete(entry.id)}
														sx={{ minWidth: 40, minHeight: 40 }}
													>
														<DeleteOutlineIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</span>
									</>
								)}
							</Box>
						</Box>

						<OccurrenceHistory times={times} formatDetailed={formatDetailed} />

						<Box sx={{ ...errorLogMetaRowSx, pl: 0.75 }}>
							{entry.provider ? (
								<Chip
									size="small"
									label={
										entry.providerRole
											? `${entry.provider} · ${entry.providerRole}`
											: entry.provider
									}
									sx={chipSx(ERROR_LOG_PROVIDER, theme)}
								/>
							) : null}
							{entry.layer ? (
								<Chip
									size="small"
									label={layerLabel(entry.layer)}
									sx={chipSx(
										externalDataLayerAccent(entry.layer) ?? ERROR_LOG_ACCENT,
										theme
									)}
								/>
							) : null}
							{entry.leagueCode ? (
								<Chip
									size="small"
									sx={chipWithLogosSx(ERROR_LOG_LEAGUE, theme)}
									label={
										<Box
											component="span"
											sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45 }}
										>
											<ErrorLogChipLogo
												src={pathToLogoImage(entry.leagueCode)}
												alt={entry.leagueCode}
											/>
											{entry.leagueCode}
										</Box>
									}
								/>
							) : null}
							{entry.matchday != null ? (
								<Chip
									size="small"
									label={t('errorLogsMatchdayChip', { number: entry.matchday })}
									sx={chipSx(ERROR_LOG_LEAGUE, theme)}
								/>
							) : null}
							{hasTeams ? (
								<Tooltip title={teamsLabel}>
									<Chip
										size="small"
										sx={chipWithLogosSx(ERROR_LOG_TEAMS, theme)}
										label={
											<MatchTeamsWithLogos
												home={{
													title: homeTitle,
													logoKey: entry.homeTeamLogoKey || undefined,
												}}
												away={{
													title: awayTitle,
													logoKey: entry.awayTeamLogoKey || undefined,
												}}
												height={14}
												sx={{ fontSize: '0.75rem' }}
											/>
										}
									/>
								</Tooltip>
							) : null}
							{entry.externalMatchId ? (
								<Chip
									size="small"
									label={`ext ${entry.externalMatchId}`}
									sx={chipIdSx(theme)}
								/>
							) : null}
							{entry.matchScheduleId ? (
								<Tooltip title={t('apiSandbox.copy')}>
									<Chip
										size="small"
										onClick={() => {
											void copyText(entry.matchScheduleId!).then(() => {
												dispatch(
													showSuccessSnackbar({
														message: t('apiSandbox.copied'),
														duration: 1600,
													})
												);
											});
										}}
										label={
											<Box component="span" sx={{ display: 'inline', userSelect: 'none' }}>
												<Box component="span" sx={{ mr: 0.5 }}>
													id
												</Box>
												<Box component="span">{entry.matchScheduleId}</Box>
											</Box>
										}
										sx={[chipIdSx(theme), { cursor: 'pointer' }] as SxProps<Theme>}
									/>
								</Tooltip>
							) : null}
							{entry.season ? (
								<Chip
									size="small"
									label={entry.season}
									sx={chipSx(ERROR_LOG_LEAGUE, theme)}
								/>
							) : null}
						</Box>

						{displayMessage ? (
							<Typography sx={{ ...errorLogMessageSx, pl: 0.75, whiteSpace: 'pre-wrap' }}>
								{displayMessage}
							</Typography>
						) : null}
						{failedMatches.length > 0 ? (
							<Box sx={{ pl: 0.75, mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
								{failedMatches.map((m, idx) => (
									<MatchTeamsWithLogos
										key={`${entry.id}-fm-${m.matchScheduleId ?? idx}`}
										home={{ title: m.homeTitle, logoKey: m.homeLogoKey }}
										away={{ title: m.awayTitle, logoKey: m.awayLogoKey }}
										height={16}
										sx={{ fontSize: '0.8rem' }}
									/>
								))}
							</Box>
						) : null}
						{entry.context?.details &&
						!(entry.message && entry.message.includes(entry.context.details)) ? (
							<Typography sx={{ ...errorLogMessageSx, pl: 0.75, whiteSpace: 'pre-wrap' }}>
								{entry.context.details}
							</Typography>
						) : null}
					</Box>
				);
			})}

			<CustomCalendarDialog
				open={confirmMode != null}
				onClose={() => setConfirmMode(null)}
				onSave={() => void handleConfirm()}
				title={confirmTitle}
				helperText={confirmHelper}
				buttonAcceptText={confirmAccept}
				submitting={confirming}
			/>
		</Box>
	);
}
